import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import { getCompany } from '../../../api/firebase/firestore/company/read';
import { getSecuritySettingsConfig } from '../../../api/firebase/firestore/security/read';
import { setSecurityMfaRequired } from '../../../api/firebase/function/security/securityMfaFunction';
import { companyPlan } from '../../../constants/enum';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import {
  gray40,
  gray80,
  gray100,
  primaryButtonBackgroundColor,
  red50,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { dialogText, dialogTitle, regularSm, regularXs } from '../../../constants/location';

interface Props {
  /** 代理モード / 運営時の対象企業。companyAdmin 自身は未指定でよい */
  companyId?: string;
}

const styles = {
  // .App のグローバル text-align: center を打ち消す（本文は左揃え基本・design-rules §1）
  wrapper: {
    textAlign: 'left' as const,
  },
  description: {
    ...regularSm,
    color: gray100,
    marginBottom: '8px',
  },
  caption: {
    ...regularXs,
    color: gray80,
    marginBottom: '16px',
  },
  errorText: {
    ...regularSm,
    color: red50,
    marginBottom: '12px',
  },
  statusRow: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
  },
  statusText: {
    ...regularSm,
    color: gray100,
  },
  actionButton: {
    height: '44px',
    minWidth: '140px',
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  dialogBody: {
    ...dialogText,
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
};

/**
 * 設定 > セキュリティ > MFA（多要素認証）タブ（#2984）。
 * テナント全メンバーへの MFA（認証アプリ）強制の ON/OFF を設定する。
 * 条件: セキュリティアドオン契約済み + Pro プラン以上（PRD §5-3）。
 * 各ユーザー自身の認証アプリ設定はマイページ（担当者設定）側で行う。
 */
const MfaTab: React.FC<Props> = ({ companyId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [infoText, setInfoText] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  // null = 未判定（取得失敗含む）。取得失敗をプラン不足と誤表示しないため boolean と区別する
  const [planEligible, setPlanEligible] = useState<boolean | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorText('');
    try {
      const [company, config] = await Promise.all([
        getCompany(companyId),
        getSecuritySettingsConfig(companyId),
      ]);
      const plan = company?.plan as number | undefined;
      setPlanEligible(plan === companyPlan.pro || plan === companyPlan.custom);
      setMfaRequired(config?.mfa_required === true);
    } catch (e) {
      Sentry.captureException(e);
      setErrorText(e instanceof Error ? e.message : 'MFA 設定の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleToggle = async (): Promise<void> => {
    setSaving(true);
    setErrorText('');
    setSnackbarOpen(false);
    try {
      await setSecurityMfaRequired({ company_id: companyId, required: !mfaRequired });
      setMfaRequired(prev => !prev);
      setInfoText(
        !mfaRequired
          ? '全メンバーの認証アプリ設定を必須にしました。未設定のメンバーは次回ログイン時に設定が求められます。'
          : '認証アプリの必須設定を解除しました。',
      );
      setSnackbarOpen(true);
      setConfirmOpen(false);
    } catch (e) {
      Sentry.captureException(e);
      setErrorText(e instanceof Error ? e.message : 'MFA 設定の更新に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={styles.wrapper}>
      <Typography sx={styles.description}>
        テナントの全メンバーに認証アプリ（二要素認証）の設定を必須にします。
        有効にすると、未設定のメンバーはログアウトされ、次回ログイン時に認証アプリの設定が求められます。
      </Typography>
      <Typography sx={styles.caption}>
        認証アプリの利用自体は全プランで任意に設定できます（マイページ &gt; 担当者設定）。
        全メンバーへの必須化はプロプラン以上 + セキュリティアドオン契約テナントのみ利用できます。
      </Typography>

      {errorText && <Typography sx={styles.errorText}>{errorText}</Typography>}

      {planEligible === false && (
        <Typography sx={styles.errorText}>
          現在の契約プランでは必須化を利用できません（プロプラン以上が必要です）。
        </Typography>
      )}

      <Box sx={styles.statusRow}>
        <Typography component="span" sx={styles.statusText}>
          全メンバー必須
        </Typography>
        {/* 切替はトグルで表現し、確定は確認ダイアログで行う（クリック時点では状態を変えない） */}
        <Switch
          checked={mfaRequired}
          onChange={(): void => setConfirmOpen(true)}
          disabled={saving || planEligible !== true}
          inputProps={{ 'aria-label': '全メンバー必須' }}
        />
      </Box>

      <Dialog open={confirmOpen} onClose={(): void => setConfirmOpen(false)} sx={styles.dialog}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitle}>
            認証アプリを{mfaRequired ? '任意に戻しますか？' : '全メンバー必須にしますか？'}
          </Typography>
          <IconButton onClick={(): void => setConfirmOpen(false)} disabled={saving} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography sx={styles.dialogBody}>
          {mfaRequired
            ? '解除すると、各メンバーの認証アプリ利用は任意になります（設定済みのメンバーはそのまま利用できます）。'
            : '有効にすると、認証アプリ未設定のメンバーはログアウトされ、設定が完了するまで Gen Drive を利用できなくなります（あなた自身が未設定の場合も設定が求められます）。'}
        </Typography>
        <Box sx={styles.dialogActions}>
          <Button
            onClick={(): void => setConfirmOpen(false)}
            disabled={saving}
            sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
          >
            キャンセル
          </Button>
          {/* 取り消し系（解除）はオレンジ primary を使わない（design-rules §4） */}
          <Button
            onClick={handleToggle}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={[
              mfaRequired ? secondaryButtonBackgroundColor : primaryButtonBackgroundColor,
              styles.actionButton,
            ]}
          >
            {mfaRequired ? '解除する' : '必須にする'}
          </Button>
        </Box>
      </Dialog>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text=""
        infoText={infoText}
      />
    </Box>
  );
};

export default MfaTab;
